import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FilterType, useFiltersStore, IExtendedFiltersValues } from '../../stores/useFilters';
import { useAccordionStore } from '../../stores/useAccordionStore';
import { createPropertyToFilter, useFilters } from '../../hooks/useFilters';
import { FiltersToolbar, IFiltersValues } from '@diana/core';
import { GraphsSection } from './GraphsSection';
import { ResetButton } from '../Buttons/ResetButton';
import { HubsNames } from '../../stores/useSignalR';
import { useProfileQuery } from '../../hooks/useProfileQuery';
import useHandleExport from '../../hooks/useExport';
import AccordionSummary, { AccordionSummaryButtonGroup } from '../Accordion/AccordionSummary';
import Accordion from '../Accordion/Accordion';
import AccordionDetails from '../Accordion/AccordionDetails';
import { useFilterOptions } from '../../stores/useFilterOptions';
import { ExportButton } from '../Buttons/ExportButton';
import { PrintButton } from '../Buttons/PrintButton';
import { Aggregators } from '../Aggregators/Aggregators';
import { PrintAPI } from '../../apis/Print';
import {
  removeQueryIfCurrentSession,
  resetBmtNumber,
  removeQueryIfNoDevices,
  convertFiltersToQuery,
  updateFiltersForBunkeringDashboard,
  convertSingleDeviceFilterToQuery,
} from '../../utils/convertFiltersToQuery';
import { useMessageStore, registerFiltersStore } from '../../stores/useMessageStore';
import { getDeviceName, getExportFileName, isEmerson } from '../../utils/stringUtils';
import { ConfirmationModal } from '../Modals/ConfirmationModal';
import { ErrorModal } from '../Modals/ErrorModal';
import { useTranslation } from 'react-i18next';
import { LIVE_SESSION_NS } from './i18n';
import { useSignalRHubStore } from '../../stores/useSignalR';
import { diagnosePrintError, liveLinkFrom, PrintDiag } from '../../utils/diagnosePrintError';
import { transformAccordionToFilters } from '../../utils/transformAccordionToFilters';
import { CalendarDatePickerPreset } from '@diana/core';
import { getDefaultTimeRange } from '../../utils/timeRangeUtils';
import { cloneDeep } from 'lodash';
import { isCloud } from '../../utils/urlUtils';

const BUNKERING_DASHBOARD_STORE_ID = 'bunkering-dashboard';

const BunkeringDashboard = () => {
  const { t } = useTranslation([LIVE_SESSION_NS]);

  const { breakpoints } = useTheme();
  const resetMessages = useMessageStore((state) => state.resetHubMessages);
  const bunkeringLogs = useMessageStore(
    (state) => state.messages?.[HubsNames.bunkeringStatusLogs] || [],
  );
  const messagesLength = useMessageStore(
    (state) => state.messages?.[HubsNames.bunkeringDashboard]?.length,
  );
  const resetMessagesMemoized = useCallback(
    () => resetMessages(HubsNames.bunkeringDashboard),
    [resetMessages],
  );

  const [finalFilters, setFinalFilters] = useState<IExtendedFiltersValues | null>(null);

  const filtersStore = useFiltersStore(BUNKERING_DASHBOARD_STORE_ID, {
    timeRange: getDefaultTimeRange(CalendarDatePickerPreset.LastSixHours),
    barges: [],
  });

  
  useEffect(() => {
    registerFiltersStore(HubsNames.bunkeringDashboard, filtersStore);
  }, [filtersStore]);

  const {
    filtersValues,
    availableFilters,
    setAvailableFilters,
    setFilters,
    setFiltersValues,
    resetData,
    isTimeRangeEndsBeforeNow,
  } = filtersStore;

  const {
    expandedAccordions,
    activeButtons,
    setExpandedAccordion,
    initializeAccordion,
    setSelectedButton,
  } = useAccordionStore();

  const setStreamArgs = useSignalRHubStore((state) => state.setStreamArgs);
  const disposeStream = useSignalRHubStore((state) => state.disposeStream);

  const { dataGroupedByBarge } = useFilterOptions();
  const isMobile = useMediaQuery(breakpoints.up(700));
  const { defaultData, onFilterChange } = useFilters({
    createPropertyToFilter,
    filtersValues: filtersValues as IExtendedFiltersValues,
    setFiltersValues,
    availableFilters: availableFilters.filter(
      (filter) => filter.property.key === FilterType.TIME_RANGE,
    ),
  });
  const { refetch, isFetching } = useProfileQuery({
    ...filtersValues,
    ...finalFilters,
  } as IExtendedFiltersValues);
  const handleExport = useHandleExport(refetch);

  const onResetAllClick = useCallback(() => {
    resetData();
  }, [resetData]);

  const accordionGroup = useMemo(() => {
    return (
      dataGroupedByBarge?.barges.map((barge) => ({
        name: barge.name,
        buttons: barge.devices.map((device) => ({
          name: getDeviceName({ barge, device }),
          displayName: device.name,
          value: device.id,
        })),
      })) || []
    );
  }, [dataGroupedByBarge]);

  const updateStreamArgs = useCallback(() => {
    const timeRangeValue = cloneDeep(filtersValues[FilterType.TIME_RANGE] || {});
    const transformedFilters = transformAccordionToFilters(
      expandedAccordions,
      activeButtons,
      timeRangeValue,
      dataGroupedByBarge,
    );

    const transformers = [
      resetBmtNumber,
      removeQueryIfCurrentSession,
      removeQueryIfNoDevices,
      (filters: IFiltersValues) => updateFiltersForBunkeringDashboard(filters, bunkeringLogs),
    ];

    const finalFilters = transformers.reduce(
      (currentFilters, transformer) => transformer(currentFilters),
      transformedFilters,
    );

    if (finalFilters !== null) {
      setFinalFilters(finalFilters);
      // Update bunkeringDashboard stream
      setStreamArgs(HubsNames.bunkeringDashboard, convertFiltersToQuery(finalFilters));
    }

    // Update aggregateDashboard stream with its specific transformers
    const aggregateTransformers = [resetBmtNumber, removeQueryIfNoDevices];
    const aggregateFilters = aggregateTransformers.reduce(
      (currentFilters, transformer) => transformer(currentFilters),
      transformedFilters,
    );

    if (aggregateFilters !== null) {
      setStreamArgs(HubsNames.aggregateDashboard, convertFiltersToQuery(aggregateFilters));
    }

    const timeRange = transformedFilters[FilterType.TIME_RANGE];

    if (
      timeRange &&
      timeRange.length > 0 &&
      timeRange[0].preset === CalendarDatePickerPreset.CurrentSession
    ) {
      // Detailed profile graphs are replaced by the active-session stream, so
      // dispose bunkeringDashboard. aggregateDashboard must stay subscribed — it
      // is the only SubscribeProfileSummary source feeding the Aggregators, which
      // are shown for live sessions (isTimeRangeEndsBeforeNow() === false).
      // Disposing it sets isDisposed=true, so addMessage drops every summary
      // message and the cards render empty ("--" / "Non Applicable").
      disposeStream(HubsNames.bunkeringDashboard);

      if (convertSingleDeviceFilterToQuery(transformedFilters) !== null) {
        setStreamArgs(
          HubsNames.activeSessionBunkeringDashboard,
          convertSingleDeviceFilterToQuery(transformedFilters),
        );
      }
    }
  }, [
    filtersValues,
    expandedAccordions,
    activeButtons,
    bunkeringLogs,
    setStreamArgs,
    disposeStream,
    dataGroupedByBarge,
  ]);

  const handleAccordionChange = useCallback(
    (panel: string, buttons: any) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      resetMessagesMemoized();
      setExpandedAccordion(panel, isExpanded, buttons);
    },
    [resetMessagesMemoized, setExpandedAccordion],
  );

  const handleButtonClick = useCallback(
    (bargeName: string, item: any) => {
      resetMessagesMemoized();
      setSelectedButton(bargeName, item);
    },
    [resetMessagesMemoized, setSelectedButton],
  );

  useEffect(() => {
    initializeAccordion(accordionGroup);
  }, [accordionGroup, initializeAccordion]);

  useEffect(() => {
    updateStreamArgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedAccordions, activeButtons, filtersValues]);

  useEffect(() => {
    setStreamArgs(HubsNames.bunkeringStatusLogs, null);
    return () => {
      resetMessagesMemoized();
      disposeStream(HubsNames.bunkeringDashboard);
      disposeStream(HubsNames.aggregateDashboard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetMessagesMemoized, disposeStream]);

  const [showExportModal, setShowExportModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [printError, setPrintError] = useState<PrintDiag | null>(null);
  const [printingDeviceId, setPrintingDeviceId] = useState<string | null>(null);
  const printInFlightRef = useRef(false);
  const [printRequested, setPrintRequested] = useState(false);
  const [exportingBarge, setExportingBarge] = useState<{
    bargeName: string;
    deviceName: string;
    deviceId: string;
  } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportClick = useCallback(
    (bargeName: string, deviceName: string, deviceId: string) => {
      setExportingBarge({ bargeName, deviceName, deviceId });
      setShowExportModal(true);
    },
    [],
  );

  const handleExportConfirm = useCallback(async () => {
    if (!exportingBarge) return;

    try {
      setIsExporting(true);
      const fileName = getExportFileName({
        bargeName: exportingBarge.bargeName,
        deviceName: exportingBarge.deviceName,
      });

      await handleExport(fileName);
      setIsExporting(false);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
      setShowExportModal(false);
      requestAnimationFrame(() => {
        setShowErrorModal(true);
      });
    }
  }, [exportingBarge, handleExport]);

  const handleExportRetry = useCallback(() => {
    setShowErrorModal(false);
    setShowExportModal(true);
  }, []);

  const handlePrintClick = useCallback(
    async (bargeName: string, deviceName: string, deviceId: string) => {
      if (!deviceId || printInFlightRef.current) return; // synchronous guard against double prints

      printInFlightRef.current = true;
      setPrintingDeviceId(deviceId);
      try {
        await PrintAPI.print({ deviceId });
        setPrintRequested(true);
      } catch (error) {
        const link = liveLinkFrom(
          Object.values(useSignalRHubStore.getState().hubs).map((h) => h?.connection?.state),
        );
        const diag = diagnosePrintError(error, link);
        setPrintError(diag);
        console.error(`Print failed [${diag.code}]:`, error);
      } finally {
        printInFlightRef.current = false;
        setPrintingDeviceId(null);
      }
    },
    [],
  );

  return (
    <Box
      sx={{ mt: '10px', mb: '5px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Box
        component="section"
        display="flex"
        justifyContent="center"
        sx={{ '.MuiStack-root': { flex: 1, minWidth: 0 } }}
      >
        <FiltersToolbar
          availableFilters={availableFilters
            .filter((filter) => filter.property.key === FilterType.TIME_RANGE)
            .map((filter) => createPropertyToFilter(filter.property, setFiltersValues))}
          defaultData={defaultData}
          filters={[FilterType.TIME_RANGE]}
          filtersValues={filtersValues}
          onFilterChange={onFilterChange}
          setFilters={setFilters}
          setAvailableFilters={setAvailableFilters}
          setFiltersValues={setFiltersValues}
          showAddButton={false}
          showResetButton={false}
          showRemoveButton={false}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginTop: '0',
            height: isMobile ? '36px' : 'auto',
            paddingBottom: isMobile ? 0 : '10px',
          }}
        >
          <ResetButton isDisabled={true} onResetAllClick={onResetAllClick} />
        </Box>
      </Box>
      <Box className="content">
        {accordionGroup.map(({ name, buttons }) => {
          const isExpanded = expandedAccordions.includes(name);
          const activeButton = activeButtons?.[name];
          const hasActiveDevice = !!activeButton && messagesLength;

          return (
            <Accordion
              key={name}
              className="content"
              sx={{ marginBottom: '16px' }}
              expanded={isExpanded}
              onChange={handleAccordionChange(name, buttons)}
            >
              <AccordionSummary aria-controls={`${name}-content`} id={`${name}-header`}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography align="center" variant="h4" sx={{ marginRight: '16px' }}>
                    {name}
                  </Typography>
                  <AccordionSummaryButtonGroup
                    onClick={(_, item) => handleButtonClick(name, item)}
                    activeButtonValue={activeButton?.value ?? null}
                    buttons={buttons}
                    dotIds={bunkeringLogs.map((log) => log.deviceId)}
                  />
                </Box>
                <PrintButton
                  onHandlePrint={() =>
                    handlePrintClick(name, activeButton?.displayName || '', activeButton?.value)
                  }
                  disabled={printingDeviceId === activeButton?.value}
                  isVisible={!isCloud()} // We will add user role check on the barge (write-high) later
                />
                <ExportButton
                  isLoading={isFetching}
                  onHandleExportData={() =>
                    handleExportClick(name, activeButton?.displayName || '', activeButton?.value)
                  }
                  disabled={!hasActiveDevice}
                />
              </AccordionSummary>
              <AccordionDetails>
                {isExpanded && (
                  <Aggregators
                    showAggregators={isTimeRangeEndsBeforeNow()}
                    isEmerson={isEmerson(activeButton?.displayName)}
                  />
                )}
                {isExpanded && (
                  <GraphsSection
                    isEmerson={isEmerson(activeButton?.displayName)}
                    timeRange={filtersValues[FilterType.TIME_RANGE]}
                    filtersValues={finalFilters}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <ConfirmationModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        title={t('components/BunkeringDashboard:modals.export.title', {
          bargeName: exportingBarge?.bargeName,
          deviceName: exportingBarge?.deviceName,
        })}
        message={t(
          isExporting
            ? 'components/BunkeringDashboard:modals.export.exportingMessage'
            : 'components/BunkeringDashboard:modals.export.message',
          {
            bargeName: exportingBarge?.bargeName,
            deviceName: exportingBarge?.deviceName,
          },
        )}
        confirmText={t('components/BunkeringDashboard:modals.export.confirmText')}
        cancelText={t('components/BunkeringDashboard:modals.export.cancelText')}
        isLoading={isExporting}
      />

      <ErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onRetry={handleExportRetry}
        message={t('components/BunkeringDashboard:modals.export.error')}
      />

      <ErrorModal
        open={!!printError}
        onClose={() => setPrintError(null)}
        title={t('components/BunkeringDashboard:modals.print.title')}
        message={
          printError
            ? t(`components/BunkeringDashboard:modals.print.${printError.messageKey}`)
            : ''
        }
      />

      <Snackbar
        open={printRequested}
        autoHideDuration={4000}
        onClose={() => setPrintRequested(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setPrintRequested(false)} severity="success" sx={{ width: '100%' }}>
          {t('components/BunkeringDashboard:modals.print.success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default memo(BunkeringDashboard, () => true);
